// Libraries
import { Component, OnInit, OnChanges, Input, Output, EventEmitter } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/range';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/toArray';

/**
 * `PaginationComponent` implements a view for manage a list with pagination.
 */
@Component({
  selector: 'wp-pagination',
  styles: [`
    .wp-pagination-wrapper .wp-pagination-text-wrapper {
      margin-left: 10px;
      padding-left: 10px;
      border-left: 1px solid #e7ecf1;
    }
  `],
  template: `
    <div class="wp-pagination-wrapper">
      <nav class="d-inline-block align-middle" aria-label="...">
        <ul class="pagination {{paginationClasses}} mb-0">
          <li class="page-item" [class.disabled]="currentPage == 1 || disabled">
            <a class="page-link" href="#" (click)="selectPage(1, $event)">
              <span aria-hidden="true">&laquo;</span>
              <span class="sr-only">First</span>
            </a>
          </li>
          <li class="page-item" [class.disabled]="currentPage == 1 || disabled">
            <a class="page-link" href="#" (click)="selectPage(currentPage - 1, $event)">
              <span aria-hidden="true">&lsaquo;</span>
              <span class="sr-only">Previous</span>
            </a>
          </li>
          <li
            class="page-item" *ngFor="let page of pages | async"
            [class.active]="page == currentPage"
            [class.disabled]="disabled">
            <a class="page-link" href="#" (click)="selectPage(page, $event)">
              {{ page }}
              <span class="sr-only">(current)</span>
            </a>
          </li>
          <li class="page-item" [class.disabled]="currentPage == totalPages || disabled">
            <a class="page-link" href="#" (click)="selectPage(currentPage + 1, $event)">
              <span aria-hidden="true">&rsaquo;</span>
              <span class="sr-only">Next</span>
            </a>
          </li>
          <li class="page-item" [class.disabled]="currentPage == totalPages || disabled">
            <a class="page-link" href="#" (click)="selectPage(totalPages, $event)">
              <span aria-hidden="true">&raquo;</span>
              <span class="sr-only">Last</span>
            </a>
          </li>
        </ul>
      </nav>

      <div class="wp-pagination-text-wrapper d-inline-block align-middle" [ngPlural]="totalCount">
        <ng-template ngPluralCase="=0">No hay resultados</ng-template>
        <ng-template ngPluralCase="=1">Viendo 1 entrada</ng-template>
        <ng-template ngPluralCase="other">Viendo {{ count }} de {{ totalCount }} entradas</ng-template>
      </div>
    </div>
  `
  // templateUrl: 'pagination.component.html' // TODO: REVIEW THIS
})
export class PaginationComponent implements OnInit, OnChanges {
  /**
   * Current page.
   */
  @Input() public currentPage = 1;
  /**
   * Per page.
   */
  @Input() public perPage = 10;
  /**
   * Result count.
   */
  @Input() public count = 0;
  /**
   * Total count.
   */
  @Input() public totalCount = 0;
  /**
   * Pagination element classes.
   */
  @Input() public paginationClasses = '';
  /**
   * Disable component.
   */
  @Input() public disabled = false;
  /**
   * Page change event.
   */
  @Output() public pageChange: EventEmitter<number> = new EventEmitter<number>();
  /**
   * Current page list
   */
  public pages: Observable<number[]>;
  /**
   * Current total pages
   */
  public totalPages: number;
  /**
   * Saved total count
   */
  protected savedTotalCount: number;

  /**
   * On init method, generate all pages
   */
  public ngOnInit(): void {
    // Generate pages
    this.generatePages();
  }

  /**
   * On changes method, generate all pages
   */
  public ngOnChanges(): void {
    // Generate pages
    this.generatePages();
  }

  /**
   * Generate pages array based on attributes.
   */
  public generatePages(): void {
    // Calculate total pages and page array list
    this.totalPages = this.getTotalPages(this.perPage, this.totalCount);
    this.pages = Observable.range(0, 3)
      .map((currentOffset: any) => {
        if ((this.currentPage + 1) > this.totalPages) {
          return this.currentPage - 2 + currentOffset;
        } else if ((this.currentPage + 2) > this.totalPages) {
          return this.currentPage - 1 + currentOffset;
        } else if (this.currentPage > 1) {
          return this.currentPage + currentOffset - 1;
        } else {
          return this.currentPage + currentOffset;
        }
      })
      .filter((page: number) => this.isValidPageNumber(page, this.totalPages))
      .toArray();
    // Save total count
    this.savedTotalCount = this.totalCount;
  }

  /**
   * Check if page number is valid.
   * @param page Page numer to check
   * @param totalPages Total pages
   * @returns TRUE If page is available.
   */
  public isValidPageNumber(page: number, totalPages: number): boolean {
    return page > 0 && page <= totalPages;
  }

  /**
   * Returns the number of total pages.
   * @param perPage Page limit
   * @param totalCount Total count
   * @returns Total pages number.
   */
  public getTotalPages(perPage: number, totalCount: number): number {
    return Math.ceil(Math.max(totalCount, 1) / Math.max(perPage, 1));
  }

  /**
   * Select one page.
   * @param page Page number to select
   * @param event Current event
   */
  public selectPage(page: number, event: any): void {
    this.cancelEvent(event);
    // Check if page is valid
    if (this.isValidPageNumber(page, this.totalPages)) {
      // Emit event
      this.pageChange.emit(page);
    }
  }

  /**
   * Prevent current event.
   * @param event Current event
   */
  public cancelEvent(event: any): void {
    event.preventDefault();
  }
}
